import { z } from 'zod';
import { optionTypeSchema } from '../../helper';
import { ITrip, tripSchema } from './trip';
import { ICarrier } from '../masterData/carrier/carrier';
import { ITruckType } from '../masterData/truckType/truckType';
import { IDispatchInstruction } from './dispatchInstruction';
import { IStock } from '../stock/stock';
import { sum, uniq } from 'lodash';
import { IDispatchView } from './dispatchView';
import { ITemperatureUnit } from './temperatureUnit';
import isContainer from 'is-container';
import { format } from 'date-fns';
import { v1 } from 'uuid';
import { DATEPICKER_FORMAT_DEFAULT_DATE_FNS } from '../../../appConstants';

export const tripFormSchema = z.object({
    id: z.number().default(0),
    guid: z.string().default(v1()),
    isActive: z.boolean().default(true),

    description: z.string().min(1, 'Description is required'),
    loadDate: z.date(),
    driver: z.string().optional().default(''),
    container: z
        .string()
        .optional()
        .refine(val => !val || val.length === 11, { message: 'Container Number length must be 11 characters.' })
        .refine(val => !val || /^[A-Z]{3}/.test(val), { message: 'First three characters must be uppercase letters, see info button.' })
        .refine(val => !val || /^[A-Z]{3}[UJZ]{1}/.test(val), { message: 'Forth character can only be U, J or Z, see info button.' })
        .refine(val => !val || /[0-9]{7}$/.test(val), { message: 'Last 7 characters must be numbers, see info button.' })
        .refine(val => !val || isContainer(val), { message: 'Invalid container number, see info button.' }),
    containerTareWeight: z
        .union([z.number(), z.string()])
        .optional()
        .refine(val => val === undefined || val === '' || Number(val) >= 0, { message: 'Tare weight must be a positive number' }),
    containerGrossWeight: z.union([z.number(), z.string()]).optional(),
    sealNumber: z.string().optional().default(''),
    registrationNumber: z.string().optional().default(''),
    carrier: optionTypeSchema.nullable().optional(),
    temperatureUnits: z.array(
        // Looser schema for form editing; API validation happens on submit via tripSchema
        z.object({
            id: z.number().optional().default(0),
            guid: z.string().optional().default(''),
            isActive: z.boolean().optional().default(true),
            deviceNumber: z.string(),
            stockId: z.number(),
            tripId: z.number().optional().default(0),
        })
    ).default([]),
    truckType: optionTypeSchema.nullable().optional(),
    fleetNumber: z.string().optional().default(''),
});

export type ITripFormValues = z.infer<typeof tripFormSchema>;

export const tripDefaultFormSchema = tripFormSchema.extend({
    // allow undefined loadDate during initial parse, will be set to new Date()
    loadDate: z.date().optional().default(new Date()),
    // provide an initial empty string for description while allowing the form resolver to enforce min length
    description: z.string().default(''),
});

export const TripFormValues = (
    trip ?: ITrip | null,
    carriers ?: Array<ICarrier>,
    truckTypes ?: Array<ITruckType>,
    tripLoadDate ?: string,
    dispatchView ?: Array<IDispatchView>,
    dispatches ?: Array<IDispatchInstruction>,
    stocks ?: Array<IStock>,
) : ITripFormValues => {
    let containerGrossWeight : string | number | undefined;
    if (dispatchView) {
        const stockWeight = sum(dispatchView?.map(x => x.palletsTotalWeight));
        containerGrossWeight = trip?.containerTareWeight ? ((trip?.containerTareWeight ?? 0) + stockWeight) : '';
    } else {
        const stockIds : Array<number> = [];
        dispatches?.filter(x => x.isActive).forEach(x => x.dispatchLines.filter(y => y.isActive).forEach(y => stockIds.push(y.currentStockId)));
        const stockWeight = sum(stocks?.filter(x => x.isActive && uniq(stockIds).some(y => y === x.id)).map(x => x.grossWeight));
        containerGrossWeight = trip?.containerTareWeight ? ((trip?.containerTareWeight ?? 0) + stockWeight) : '';
    }

    if (!trip) {
        return tripDefaultFormSchema.parse({
            containerGrossWeight,
        });
    }

    const mapped : Partial<ITripFormValues> = {
        id: trip.id,
        guid: trip.guid,
        isActive: trip.isActive,
        description: trip.description ?? '',
        loadDate: tripLoadDate ? new Date(tripLoadDate) : (trip.loadDate ? new Date(trip.loadDate as unknown as string) : new Date()),
        driver: trip.driver ?? '',
        container: trip.container ?? '',
        containerTareWeight: (trip as any).containerTareWeight === null || (trip as any).containerTareWeight === undefined ? '' : (trip as any).containerTareWeight,
        containerGrossWeight,
        sealNumber: trip.sealNumber ?? '',
        registrationNumber: trip.registrationNumber ?? '',
        temperatureUnits: (trip.temperatureUnits as Array<ITemperatureUnit>) ?? [],
        fleetNumber: trip.fleetNumber ?? '',
        carrier: carriers && trip.carrierId
            ? {
                label: carriers.find(x => x.id === trip.carrierId)?.name ?? '',
                value: trip.carrierId,
            }
            : undefined,
        truckType: truckTypes && trip.truckTypeId
            ? {
                label: truckTypes.find(x => x.id === trip.truckTypeId)?.code ?? '',
                value: trip.truckTypeId,
            }
            : undefined,
    };

    return tripDefaultFormSchema.parse(mapped);
};

export const TripFrom = (formValues : ITripFormValues) : ITrip => {
    const carrierIdRaw = formValues.carrier?.value;
    const carrierIdParsed = carrierIdRaw !== undefined && carrierIdRaw !== null && carrierIdRaw !== '' ? Number(carrierIdRaw) : undefined;
    const carrierId = Number.isFinite(Number(carrierIdParsed)) ? (carrierIdParsed as number) : undefined;

    const truckTypeIdRaw = formValues.truckType?.value;
    const truckTypeIdParsed = truckTypeIdRaw !== undefined && truckTypeIdRaw !== null && truckTypeIdRaw !== '' ? Number(truckTypeIdRaw) : undefined;
    const truckTypeId = Number.isFinite(Number(truckTypeIdParsed)) ? (truckTypeIdParsed as number) : undefined;

    const tareRaw = (formValues as any).containerTareWeight as unknown;
    const containerTareWeight = tareRaw === undefined || tareRaw === null || tareRaw === ''
        ? undefined
        : (Number.isFinite(Number(tareRaw)) ? Number(tareRaw) : undefined);

    return tripSchema.parse({
        id: formValues.id,
        guid: formValues.guid,
        isActive: formValues.isActive,
        description: formValues.description,
        loadDate: format(formValues.loadDate, DATEPICKER_FORMAT_DEFAULT_DATE_FNS),
        driver: formValues.driver,
        container: formValues.container,
        containerTareWeight,
        sealNumber: formValues.sealNumber,
        registrationNumber: formValues.registrationNumber,
        carrierId,
        temperatureUnits: (formValues.temperatureUnits as Array<ITemperatureUnit>) ?? [],
        truckTypeId,
        fleetNumber: formValues.fleetNumber,
    });
};
